import { ref } from 'vue'
import { useUsersStore } from '@/stores/modules/users.ts'
import { useBackendStore } from '@/stores/system/backend'
import { buffer2Base64 } from '@/helpers/Func'

/**
 * Ein Bild pro User nur einmal laden und umrechnen. Vorher lud jede Kommentar-/Listenzeile
 * das gleiche Profilbild erneut (Dashboard: 16× dieselbe Anfrage + Base64-Umrechnung).
 * Schlüssel `<mandant>:<user-id>`; geleert bei Identitätswechsel (Login, Logout, „Anmelden als“,
 * LoginAs) und nach dem Hochladen eines neuen Bildes.
 */
const imageCache = new Map<string, Promise<string | null>>()

export function clearProfileImageCache(userId?: number) {
	if (userId === undefined) {
		imageCache.clear()
		return
	}
	for (const key of [...imageCache.keys()])
		if (key.endsWith(`:${userId}`)) imageCache.delete(key)
}

export function useProfileImage() {
	const userStore = useUsersStore()
	const backendStore = useBackendStore()
	const loadingProfiIeImage = ref(false)
	const hasProfileImage = ref(false)
	const profileImage = ref<string | undefined | null>()

	const loadProfileImage = async (user?: Api.User | Api.WhoAmI | null) => {
		if (!user?.profile_image) {
			hasProfileImage.value = false
			profileImage.value = null
			return
		}
		hasProfileImage.value = true
		loadingProfiIeImage.value = true
		const key = `${backendStore.url?.subdomain ?? ''}:${user.id}`
		try {
			let pending = imageCache.get(key)
			if (!pending) {
				pending = userStore
					.loadImage(user.id)
					.then((img) => buffer2Base64(img) ?? null)
				imageCache.set(key, pending)
				// Fehlschlag nicht cachen, damit ein späterer Versuch neu lädt
				pending.catch(() => imageCache.delete(key))
			}
			profileImage.value = await pending
		} catch {
			profileImage.value = null
		} finally {
			loadingProfiIeImage.value = false
		}
	}

	return {
		loadProfileImage,
		hasProfileImage,
		profileImage,
		loadingProfiIeImage,
	}
}
